import React, { useState } from 'react';
import {
  View, TextInput, Pressable, Modal as RNModal, ScrollView, Platform,
  type StyleProp, type ViewStyle, type TextStyle, type TextInputProps,
} from 'react-native';
import { useTheme } from '../theme';
import Txt from './Txt';
import Icon from './Icon';

type InputSize = 'sm' | 'md' | 'lg';
const HEIGHT: Record<InputSize, number> = { sm: 28, md: 34, lg: 40 };
const PADX: Record<InputSize, number> = { sm: 10, md: 12, lg: 14 };
const FS: Record<InputSize, 'sm' | 'md' | 'lg'> = { sm: 'sm', md: 'md', lg: 'lg' };

interface InputProps extends Omit<TextInputProps, 'style'> {
  size?: InputSize;
  /** Multiline textarea (min-height 80). */
  multilineRows?: number;
  /** Accepts text styles (fontSize/textAlign/fontFamily…) since it forwards to a TextInput. */
  style?: StyleProp<TextStyle>;
}

/** RN port of theme.css's .t-input. Use `value`/`onChangeText` (RN), not onChange. */
export function Input({ size = 'md', multilineRows, style, ...rest }: InputProps) {
  const t = useTheme();
  const [focused, setFocused] = useState(false);
  const multiline = !!multilineRows || rest.multiline;
  return (
    <TextInput
      placeholderTextColor={t.colors.ink4}
      onFocus={(e) => { setFocused(true); rest.onFocus?.(e); }}
      onBlur={(e) => { setFocused(false); rest.onBlur?.(e); }}
      multiline={multiline}
      style={[
        {
          width: '100%',
          minHeight: multiline ? 80 : HEIGHT[size],
          height: multiline ? undefined : HEIGHT[size],
          paddingHorizontal: PADX[size],
          paddingVertical: multiline ? 8 : 0,
          borderWidth: 1,
          borderColor: focused ? t.colors.accent : t.colors.line,
          backgroundColor: t.colors.card,
          borderRadius: t.radii[2],
          fontSize: t.fontSize[FS[size]],
          color: t.colors.ink,
          fontFamily: t.font.sans,
          textAlignVertical: multiline ? 'top' : 'center',
        } as TextStyle,
        style,
      ]}
      {...rest}
    />
  );
}

interface TimeInputProps {
  value: string;               // "HH:MM" 24-hour — same shape the form validates/saves
  onChangeText: (v: string) => void;
  size?: InputSize;
  placeholder?: string;
  style?: StyleProp<TextStyle>;
}

/**
 * 24-hour time field. On web it renders a native <input type="time"> so the browser
 * shows its clock picker; on native it falls back to the plain text Input. Either way
 * the value stays an "HH:MM" string, so callers/validation/persistence are unchanged.
 */
export function TimeInput({ value, onChangeText, size = 'md', placeholder = 'HH:MM', style }: TimeInputProps) {
  const t = useTheme();
  const [focused, setFocused] = useState(false);

  if (Platform.OS === 'web') {
    // Open the browser's native picker on any click/focus of the field, not just the
    // clock glyph. showPicker() needs user activation, so it's safe to call here and
    // may throw in older browsers — swallow it (the icon still works as a fallback).
    const openPicker = (el: HTMLInputElement | null) => {
      try { el?.showPicker?.(); } catch { /* not supported / no activation */ }
    };
    // RNW can't render <input type="time">, so drop to the DOM element directly and
    // mirror .t-input styling. step=60 → minute precision (no seconds).
    return (
      <input
        type="time"
        value={value}
        step={60}
        onChange={(e) => onChangeText((e.target as HTMLInputElement).value)}
        onClick={(e) => openPicker(e.currentTarget)}
        onFocus={(e) => { setFocused(true); openPicker(e.currentTarget); }}
        onBlur={() => setFocused(false)}
        style={{
          cursor: 'pointer',
          width: '100%',
          height: HEIGHT[size],
          paddingLeft: PADX[size],
          paddingRight: PADX[size],
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: focused ? t.colors.accent : t.colors.line,
          backgroundColor: t.colors.card,
          borderRadius: t.radii[2],
          fontSize: t.fontSize[FS[size]],
          color: t.colors.ink,
          fontFamily: t.font.sans,
          outline: 'none',
          boxSizing: 'border-box',
        } as React.CSSProperties}
      />
    );
  }

  // Native fallback — plain numeric text entry.
  return (
    <Input
      size={size}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      keyboardType="numbers-and-punctuation"
      style={style}
    />
  );
}

export interface SelectOption {
  label: string;
  value: string;
}

interface SelectProps {
  value: string;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  size?: InputSize;
  placeholder?: string;
  style?: StyleProp<ViewStyle>;
}

/**
 * Cross-platform Select. The web <select> has no RN equivalent, so this renders a field
 * that opens a modal list of options — identical behaviour on web (RNW) and native.
 */
export function Select({ value, onValueChange, options, size = 'md', placeholder = 'Select…', style }: SelectProps) {
  const t = useTheme();
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <>
      <Pressable
        onPress={() => setOpen(true)}
        style={[
          {
            width: '100%',
            height: HEIGHT[size],
            paddingHorizontal: PADX[size],
            borderWidth: 1,
            borderColor: t.colors.line,
            backgroundColor: t.colors.card,
            borderRadius: t.radii[2],
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          },
          style,
        ]}
      >
        <Txt size={FS[size]} color={selected ? 'ink' : 'ink4'}>{selected?.label ?? placeholder}</Txt>
        <Icon name="grid" size={12} color={t.colors.ink3} />
      </Pressable>
      <RNModal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <Pressable
          onPress={() => setOpen(false)}
          style={{ flex: 1, backgroundColor: 'rgba(16,24,40,0.42)', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <View style={{ width: '100%', maxWidth: 360, alignSelf: 'center', backgroundColor: t.colors.card, borderRadius: t.radii[4], maxHeight: '70%', overflow: 'hidden', ...t.shadows.lg }}>
            <ScrollView>
              {options.map((o) => (
                <Pressable
                  key={o.value}
                  onPress={() => { onValueChange(o.value); setOpen(false); }}
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    borderBottomWidth: 1,
                    borderBottomColor: t.colors.line,
                    backgroundColor: o.value === value ? t.colors.accentSoft : 'transparent',
                  }}
                >
                  <Txt color={o.value === value ? 'accentInk' : 'ink'}>{o.label}</Txt>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        </Pressable>
      </RNModal>
    </>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

/** RN port of theme.css's .t-field — label + control + hint/error, stacked. */
export function Field({ label, hint, error, children, style }: FieldProps) {
  return (
    <View style={[{ gap: 4 }, style]}>
      <Txt size="sm" weight="medium" color="ink2">{label}</Txt>
      {children}
      {hint && <Txt size="xs" color="ink3">{hint}</Txt>}
      {error && <Txt size="xs" color="danger">{error}</Txt>}
    </View>
  );
}
